import { useEffect } from "react";
import HomePage from "@/components/landing/HomePage";
import PricingPage from "@/components/landing/pricing/PricingPage";
import CompanyPage from "@/components/landing/company/CompanyPage";
import ContactPage from "@/components/landing/contact/ContactPage";
import TrustPage from "@/components/landing/trust/TrustPage";
import AIPlatformPage from "@/components/landing/ai-platform/AIPlatformPage";
import TradingPlatformPage from "@/components/landing/trading-platform/TradingPlatformPage";
import PlatformPage from "@/components/landing/platform/PlatformPage";
import LaunchPlatformPage from "@/components/landing/launch-platform/LaunchPlatformPage";
import ResourcesPage from "@/components/landing/resources/ResourcesPage";
import GetStartedPage from "@/components/landing/get-started/GetStartedPage";
import WlSignupSuccessPage from "@/components/landing/wl-signup/WlSignupSuccessPage";
import SiteFrame from "@/components/landing/SiteFrame";
import BrandLogo from "@/components/landing/BrandLogo";
import { Container, GlassCard, GradientButton, GhostButton, SectionHeading } from "@/components/landing/design-system";
import { Link } from "react-router-dom";

/* TODO: have your lawyer rewrite for Kalki Ai Cloud */

const Meta = ({ title, description }) => {
  useEffect(() => {
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", description);
  }, [title, description]);
  return null;
};

export const HomeRoute = () => (
  <>
    <Meta title="Kalki Ai Cloud — Trade Smarter. Win Bigger." description="Enterprise AI trading infrastructure — AI mentor, trading terminal, backtesting, academy and white-label deployment in one unified platform." />
    <HomePage />
  </>
);

export const PlansRoute = () => (
  <>
    <Meta title="White-Label Plans — Starter $499 · Growth $949 · Enterprise $1,799 · Kalki Ai Cloud" description="White-label licensing for AI trading platform owners. Three monthly plans with the complete Kalki Ai Cloud platform included. Secure Stripe checkout." />
    <PricingPage />
  </>
);

export const CompanyRoute = () => (
  <>
    <Meta title="About Kalki Ai Cloud — Company, Mission & Team" description="The mission, leadership and milestones behind Kalki Ai Cloud — institutional-grade AI trading infrastructure." />
    <CompanyPage />
  </>
);

export const ContactRoute = () => (
  <>
    <Meta title="Contact Sales & Book a Demo — Kalki Ai Cloud" description="Book a live demo, talk to sales, or reach support at admin@kalkiai.trade." />
    <ContactPage />
  </>
);

export const TrustRoute = () => (
  <>
    <Meta title="Trust Center — Security, Privacy, Compliance · Kalki Ai Cloud" description="Security architecture, data protection, privacy commitment and platform availability at Kalki Ai Cloud." />
    <TrustPage />
  </>
);

export const AiRoute = () => (
  <>
    <Meta title="AI Trading Platform — RAG Mentor & Copilots · Kalki Ai Cloud" description="Grounded RAG mentor, prompt intelligence, automation and enterprise AI across the Kalki Ai Cloud stack." />
    <AIPlatformPage />
  </>
);

export const TradingRoute = () => (
  <>
    <Meta title="Trading Platform — Terminal, Backtest, Strategy · Kalki Ai Cloud" description="Multi-exchange execution, strategy builder, backtesting and market intelligence." />
    <TradingPlatformPage />
  </>
);

export const PlatformRoute = () => (
  <>
    <Meta title="Enterprise AI Trading Platform — 12 Modules · Kalki Ai Cloud" description="The Kalki Ai Cloud platform — 12 enterprise modules powering branded AI trading businesses." />
    <PlatformPage />
  </>
);

export const WhiteLabelRoute = () => (
  <>
    <Meta title="White-Label AI Trading Platform — Launch in Weeks · Kalki Ai Cloud" description="Launch a fully-branded AI trading, mentor and academy platform under your name. 12 modules, custom domain + SSL, multi-tenant admin." />
    <LaunchPlatformPage />
  </>
);

export const ResourcesRoute = () => (
  <>
    <Meta title="Resources — Guides, FAQs, Insights · Kalki Ai Cloud" description="Documentation, guides, FAQs and insights from Kalki Ai Cloud." />
    <ResourcesPage />
  </>
);

export const GetStartedRoute = () => (
  <>
    <Meta title="Get Started — Launch Your AI Trading Platform · Kalki Ai Cloud" description="Answer a few questions and a solutions engineer will spin up a dedicated launch plan within one business day." />
    <GetStartedPage />
  </>
);

export const WlSignupSuccessRoute = () => (
  <>
    <Meta title="Checkout Success · Kalki Ai Cloud" description="Payment received — your Kalki Ai Cloud workspace is being provisioned." />
    <WlSignupSuccessPage />
  </>
);

export const PrivacyRoute = () => (
  <SiteFrame>
    <section className="pt-32 md:pt-40 pb-24">
      <Container size="narrow">
        <GlassCard className="p-8 md:p-12 space-y-5">
          <Meta title="Privacy Policy · Kalki Ai Cloud" description="How Kalki Ai Cloud collects, uses and protects personal data." />
          <BrandLogo href={null} size="lg" />
          <SectionHeading eyebrow="Legal" title="Privacy Policy" subtitle="Last updated: placeholder — have your lawyer review before launch." />
          <div className="space-y-4 text-sm text-zinc-400 leading-relaxed">
            <p>This placeholder policy summary is provided for the Kalki Ai Cloud marketing site. It does not constitute legal advice.</p>
            <p><span className="text-white font-semibold">Data we collect.</span> Contact-form details (name, business email, company) submitted through this site, and payment data processed by Stripe under its own privacy policy.</p>
            <p><span className="text-white font-semibold">How we use it.</span> To respond to enquiries, provision white-label workspaces and operate the platform. We never sell personal data.</p>
            <p><span className="text-white font-semibold">Your rights.</span> Access, correction, export and deletion requests: admin@kalkiai.trade.</p>
          </div>
        </GlassCard>
      </Container>
    </section>
  </SiteFrame>
);

export const TermsRoute = () => (
  <SiteFrame>
    <section className="pt-32 md:pt-40 pb-24">
      <Container size="narrow">
        <GlassCard className="p-8 md:p-12 space-y-5">
          <Meta title="Terms of Service · Kalki Ai Cloud" description="The terms governing use of the Kalki Ai Cloud platform." />
          <BrandLogo href={null} size="lg" />
          <SectionHeading eyebrow="Legal" title="Terms of Service" subtitle="Last updated: placeholder — have your lawyer review before launch." />
          <div className="space-y-4 text-sm text-zinc-400 leading-relaxed">
            <p>These placeholder terms are provided for the Kalki Ai Cloud marketing site. They do not constitute legal advice.</p>
            <p><span className="text-white font-semibold">Subscriptions.</span> White-Label plans are billed monthly via Stripe and renew automatically until cancelled. Upgrades and downgrades are prorated by Stripe.</p>
            <p><span className="text-white font-semibold">Acceptable use.</span> The platform may not be used to violate financial regulations in your jurisdiction.</p>
            <p><span className="text-white font-semibold">Contact.</span> Questions: admin@kalkiai.trade.</p>
          </div>
        </GlassCard>
      </Container>
    </section>
  </SiteFrame>
);

export const NotFoundRoute = () => (
  <SiteFrame>
    <section className="pt-32 md:pt-40 pb-24">
      <Container size="narrow">
        <GlassCard className="p-12 text-center space-y-6">
          <div className="text-[11px] font-mono uppercase tracking-widest text-brand-gold">404</div>
          <h1 className="text-3xl md:text-4xl font-semibold text-white">Page not found</h1>
          <p className="text-zinc-400">The page you are looking for does not exist or has moved.</p>
          <div className="flex items-center justify-center gap-3">
            <GradientButton href="/" size="lg">Back to home</GradientButton>
            <GhostButton href="/contact" size="lg">Contact us</GhostButton>
          </div>
        </GlassCard>
      </Container>
    </section>
  </SiteFrame>
);
